import React, { useState, useEffect } from 'react';
import { User, UserRole, Student, Teacher, ScaleLevel, ScaleDetail } from '../types';
import {
    XCircleIcon, CheckCircleIcon, UserIcon, MailIcon, LockClosedIcon,
    PhoneIcon, ChevronLeftIcon, AcademicCapIcon, BellIcon, ShieldCheckIcon,
    CameraIcon, SupportIcon, ChartBarIcon, FireIcon, StarIcon, LightBulbIcon,
    BookOpenIcon, ClipboardListIcon, TargetIcon
} from './icons';
import { SupportView } from './SupportView';
import { API_URL, apiFetch } from '../services/api';

interface ProfileViewProps {
    user: User;
    onUpdate: (updatedUser: User) => void;
    onBack: () => void;
}

type TabType = 'personal' | 'academic' | 'security' | 'preferences' | 'support' | 'analysis' | 'subscription';

export const ProfileView = ({ user, onUpdate, onBack }: ProfileViewProps) => {
    const [formData, setFormData] = useState({ ...user });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('personal');

    // Forgot password states
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetStep, setResetStep] = useState<'email' | 'code' | 'newPassword'>('email');
    const [resetEmail, setResetEmail] = useState(user?.email || '');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetError, setResetError] = useState('');
    const [resetSuccess, setResetSuccess] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer(prev => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    // Subscription packages from API
    const [apiPackages, setApiPackages] = useState<any[]>([]);
    const [pkgLoading, setPkgLoading] = useState(true);

    useEffect(() => {
        if (user?.role === UserRole.STUDENT) {
            apiFetch(API_URL + '/api/landing-content')
                .then(r => r.json())
                .then(data => {
                    if (data?.pricing?.packages) {
                        setApiPackages(data.pricing.packages.filter((p: any) => p.isActive !== false));
                    }
                })
                .catch(() => { })
                .finally(() => setPkgLoading(false));
        } else {
            setPkgLoading(false);
        }
    }, []);

    // Forgot password handlers
    const handleRequestCode = async () => {
        setResetLoading(true);
        setResetError('');
        try {
            const response = await apiFetch(`${API_URL}/api/password-reset/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail })
            });
            const data = await response.json();
            if (!response.ok) {
                console.error("API Error Response:", response.status, data);
            }
            if (data.success) {
                setResetStep('code');
                setResetSuccess('Doğrulama kodu e-posta adresinize gönderildi.');
                setResendTimer(60);
            } else {
                setResetError(data.error || 'Bir hata oluştu');
            }
        } catch (err: any) {
            console.error(err);
            setResetError(err.message || 'Bağlantı hatası');
        } finally {
            setResetLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        setResetLoading(true);
        setResetError('');
        try {
            const response = await apiFetch(`${API_URL}/api/password-reset/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail, code: resetCode })
            });
            const data = await response.json();
            if (data.success) {
                setResetStep('newPassword');
                setResetSuccess('Kod doğrulandı. Yeni şifrenizi girin.');
            } else {
                setResetError(data.error || 'Geçersiz kod');
            }
        } catch {
            setResetError('Bağlantı hatası');
        } finally {
            setResetLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (newPassword !== confirmPassword) {
            setResetError('Şifreler eşleşmiyor');
            return;
        }
        if (newPassword.length < 8) {
            setResetError('Şifre en az 8 karakter olmalı');
            return;
        }
        if (!/[A-Z]/.test(newPassword)) {
            setResetError('Şifre en az bir büyük harf (A-Z) içermelidir.');
            return;
        }
        if (!/[a-z]/.test(newPassword)) {
            setResetError('Şifre en az bir küçük harf (a-z) içermelidir.');
            return;
        }
        if (!/\d/.test(newPassword)) {
            setResetError('Şifre en az bir rakam (0-9) içermelidir.');
            return;
        }
        if (!/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword)) {
            setResetError('Şifre en az bir sembol/özel karakter (!@#$% vb.) içermelidir.');
            return;
        }
        setResetLoading(true);
        setResetError('');
        try {
            const response = await apiFetch(`${API_URL}/api/password-reset/reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword })
            });
            const data = await response.json();
            if (data.success) {
                setResetSuccess('Şifreniz başarıyla güncellendi!');
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setResetStep('email');
                    setResetCode('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setResetSuccess('');
                }, 2000);
            } else {
                setResetError(data.error || 'Bir hata oluştu');
            }
        } catch {
            setResetError('Bağlantı hatası');
        } finally {
            setResetLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await onUpdate(formData);
            setSuccessMessage("Profil başarıyla güncellendi!");
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'personal', label: 'Kişisel Bilgiler', icon: UserIcon },
        { id: 'analysis', label: 'Profil Analizi', icon: ChartBarIcon },

        ...(user?.role === UserRole.STUDENT ? [{ id: 'subscription', label: 'Abonelik', icon: LockClosedIcon }] : []),
        { id: 'security', label: 'Hesap & Güvenlik', icon: ShieldCheckIcon },
        { id: 'preferences', label: 'Bildirim Ayarları', icon: BellIcon },
        { id: 'support', label: 'Destek & İletişim', icon: SupportIcon },
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex flex-col font-sans animate-fade-in">
            {/* Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-30">
                <div className="w-full px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            title="Geri"
                            className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            <ChevronLeftIcon className="w-6 h-6" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Profil Ayarları</h1>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Hesabınızı buradan yönetin</p>
                        </div>
                    </div>
                    {successMessage && (
                        <div className="bg-green-50 text-green-700 px-4 py-2 rounded-xl flex items-center gap-2 border border-green-100 animate-slide-in">
                            <CheckCircleIcon className="w-5 h-5" />
                            <span className="font-bold">{successMessage}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex-1 w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8 h-full">

                    {/* Sidebar Tabs */}
                    <div className="w-full lg:w-72 flex-shrink-0 space-y-2">
                        {/* User Summary Card */}
                        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-700 mb-6 text-center">
                            <div className="relative w-24 h-24 mx-auto mb-4">
                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg ring-4 ring-white dark:ring-slate-800">
                                    {user?.name.charAt(0)}
                                </div>
                                <button title="Profil fotoğrafı değiştir" className="absolute bottom-0 right-0 p-2 bg-white dark:bg-slate-700 rounded-full shadow-md text-gray-600 dark:text-slate-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors border border-gray-100 dark:border-slate-600">
                                    <CameraIcon className="w-4 h-4" />
                                </button>
                            </div>
                            <h3 className="font-bold text-lg text-gray-900 dark:text-white">{user?.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-slate-400">{(user as Student).grade || 'Kullanıcı'}</p>
                        </div>

                        {/* Navigation */}
                        <nav className="space-y-1">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id as TabType)}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-bold transition-all duration-200 ${activeTab === tab.id
                                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/50'
                                        : 'text-gray-500 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white hover:shadow-sm'
                                        }`}
                                >
                                    <tab.icon className="w-5 h-5" />
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 p-8 min-h-[600px]">
                        {activeTab === 'support' ? (
                            <SupportView
                                userId={user?.id}
                                userName={user?.name}
                                userEmail={user?.email}
                                userRole={user?.role === UserRole.STUDENT ? 'student' : user?.role === UserRole.TEACHER ? 'teacher' : 'education_consultant'}
                            />
                        ) : activeTab === 'subscription' && user?.role === UserRole.STUDENT ? (() => {
                            const student = user as Student;
                            const isActive = student.packageStatus === 'active';
                            const remainingDays = isActive && student.packageExpiryDate
                                ? Math.max(0, Math.ceil((new Date(student.packageExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
                                : 0;
                            const packageName = student.packageName || 'Standart Paket';

                            return (
                                <div className="space-y-5 animate-fade-in">
                                    <h2 className="text-xl font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-3">Abonelik Yönetimi</h2>

                                    {/* API Paketleri - Dikey Kartlar */}
                                    <div>
                                        <div className="flex items-center justify-between mb-4 mt-2">
                                            <h3 className="text-base font-bold text-gray-800 dark:text-white">{isActive ? 'Paket Yükselt / Yenile' : 'Bir Paket Seçin'}</h3>
                                        </div>

                                        {pkgLoading ? (
                                            <div className="flex justify-center py-12">
                                                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                            </div>
                                        ) : apiPackages.length === 0 ? (
                                            <div className="bg-gray-50 dark:bg-slate-800/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-slate-700 py-12 text-center">
                                                <p className="text-sm text-gray-400 dark:text-slate-500">Şu an tanımlı paket bulunmamaktadır.</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                                                {apiPackages.map((pkg: any) => (
                                                    <div key={pkg.id} className={`relative group flex flex-col rounded-2xl border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${pkg.isPopular ? 'border-purple-400 dark:border-purple-500 bg-white dark:bg-slate-800 shadow-lg shadow-purple-100 dark:shadow-none' : 'border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-gray-200 dark:hover:border-slate-600'}`}>
                                                        {pkg.isPopular && (
                                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-[10px] font-black uppercase tracking-widest py-1 px-4 rounded-full shadow-md z-10">
                                                                En Popüler
                                                            </div>
                                                        )}

                                                        <div className="p-5 flex-1 flex flex-col">
                                                            <div className={`w-12 h-1.5 rounded-full mb-4 ${pkg.isPopular ? 'bg-purple-500' : pkg.color === 'blue' ? 'bg-blue-500' : pkg.color === 'amber' || pkg.color === 'orange' ? 'bg-amber-500' : 'bg-gray-300 dark:bg-slate-600'}`}></div>

                                                            <h4 className="font-bold text-lg text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{pkg.name}</h4>

                                                            <div className="mb-4">
                                                                <div className="flex items-baseline gap-1">
                                                                    <span className="text-3xl font-black text-gray-900 dark:text-white">{pkg.price}₺</span>
                                                                    <span className="text-sm text-gray-400 dark:text-slate-500">/{pkg.period || 'ay'}</span>
                                                                </div>
                                                            </div>

                                                            <p className="text-xs text-gray-500 dark:text-slate-400 mb-6 leading-relaxed">
                                                                {pkg.description || (pkg.features && pkg.features.length > 0 ? pkg.features.slice(0, 3).join(' · ') : 'Ayrıntılı bilgi için iletişime geçin.')}
                                                            </p>

                                                            <div className="mt-auto space-y-3">
                                                                <ul className="space-y-2 mb-6">
                                                                    {(pkg.features || []).slice(0, 4).map((feat: string, i: number) => (
                                                                        <li key={i} className="flex items-start gap-2 text-[11px] text-gray-600 dark:text-slate-300">
                                                                            <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                                                            <span>{feat}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>

                                                                <button className={`w-full py-3 rounded-xl text-sm font-bold transition-all duration-300 ${pkg.isPopular
                                                                    ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-200 dark:shadow-none transform active:scale-[0.98]'
                                                                    : 'bg-gray-900 dark:bg-slate-700 text-white hover:bg-gray-800 dark:hover:bg-slate-600 shadow-sm transform active:scale-[0.98]'
                                                                    }`}>
                                                                    {isActive ? 'Paketi Yükselt' : (student.packageStatus === 'expired' ? 'Paketi Yenile' : 'Hemen Başla')}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Kompakt Durum Kartı - Paketin Altına Alındı */}
                                    <div className={`flex items-center justify-between rounded-xl p-4 border shadow-sm ${isActive ? 'bg-emerald-50/50 dark:bg-emerald-900/10 border-emerald-100 dark:border-emerald-800/30' : 'bg-red-50/50 dark:bg-red-900/10 border-red-100 dark:border-red-800/30'}`}>
                                        <div className="flex items-center gap-4">
                                            <div className={`p-3 rounded-xl ${isActive ? 'bg-white dark:bg-slate-800 text-emerald-600 shadow-sm border border-emerald-100 dark:border-emerald-800' : 'bg-white dark:bg-slate-800 text-red-600 shadow-sm border border-red-100 dark:border-red-800'}`}>
                                                <LockClosedIcon className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2 mb-0.5">
                                                    <h3 className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Abonelik Durumu</h3>
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${isActive ? 'text-emerald-700 bg-emerald-100 dark:text-emerald-300/20' : 'text-red-700 bg-red-100 dark:text-red-300/20'}`}>
                                                        {isActive ? 'AKTİF' : 'PASİF'}
                                                    </span>
                                                </div>
                                                <p className="font-bold text-base text-gray-900 dark:text-white">
                                                    {isActive ? packageName : 'Aktif aboneliğiniz bulunmuyor'}
                                                </p>
                                                {isActive && (
                                                    <p className="text-[11px] text-gray-500 dark:text-slate-500 mt-1 font-medium">
                                                        Bitiş: {student.packageExpiryDate ? new Date(student.packageExpiryDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {isActive && (
                                            <div className="text-right pr-2">
                                                <div className={`text-3xl font-black mb-0 ${remainingDays <= 7 ? 'text-red-500 animate-pulse' : remainingDays <= 30 ? 'text-amber-500' : 'text-emerald-600'}`}>
                                                    {remainingDays}
                                                </div>
                                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">KALAN GÜN</p>
                                            </div>
                                        )}
                                    </div>

                                    {isActive && remainingDays <= 7 && (
                                        <div className="p-4 bg-red-50 dark:bg-red-500/10 rounded-xl border border-red-100 dark:border-red-500/30 flex items-center gap-3">
                                            <div className="p-2 bg-white dark:bg-slate-800 rounded-lg text-red-600 shadow-sm">
                                                <ShieldCheckIcon className="w-5 h-5" />
                                            </div>
                                            <p className="text-xs font-bold text-red-700 dark:text-red-400">
                                                Dikkat! Aboneliğiniz yakında sona erecek. Hizmet kesintisi yaşamamak için paketinizi yenileyin.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })() : (
                            <form onSubmit={handleSubmit} className="space-y-8 w-full">

                                {activeTab === 'personal' && (
                                    <div className="space-y-6 animate-fade-in max-w-3xl">
                                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-4">Kişisel Bilgiler</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="col-span-2">
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Ad Soyad</label>
                                                <input
                                                    title="Ad Soyad"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                    value={formData.name}
                                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">E-posta</label>
                                                <input
                                                    type="email"
                                                    title="E-posta"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                    value={formData.email}
                                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Telefon</label>
                                                <input
                                                    type="tel"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                    value={formData.phoneNumber || ''}
                                                    onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                                                    placeholder="0555 555 55 55"
                                                />
                                            </div>
                                            {user?.role === UserRole.STUDENT && (
                                                <>
                                                    <div>
                                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Veli Telefonu</label>
                                                        <input
                                                            type="tel"
                                                            className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                            value={(formData as Student).parentPhone || ''}
                                                            onChange={e => setFormData({ ...formData, parentPhone: e.target.value })}
                                                            placeholder="0555 555 55 55"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Veli E-posta</label>
                                                        <input
                                                            type="email"
                                                            className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                            value={(formData as Student).parentEmail || ''}
                                                            onChange={e => setFormData({ ...formData, parentEmail: e.target.value })}
                                                            placeholder="veli@ornek.com"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Yaşadığınız Şehir</label>
                                                        <input
                                                            className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium placeholder-gray-400 dark:placeholder-slate-400"
                                                            value={(formData as Student).city || ''}
                                                            onChange={e => setFormData({ ...formData, city: e.target.value })}
                                                            placeholder="Örn: Ankara"
                                                        />
                                                    </div>
                                                </>
                                            )}
                                            <div className="col-span-2">
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Hakkımda</label>
                                                <textarea
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium h-32 resize-none"
                                                    placeholder="Kendinizden bahsedin..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}



                                {activeTab === 'security' && (
                                    <div className="space-y-6 animate-fade-in max-w-3xl">
                                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-4">Güvenlik Ayarları</h2>
                                        <div className="grid grid-cols-1 gap-6 max-w-lg">
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Mevcut Şifre</label>
                                                <input
                                                    type="password"
                                                    autoComplete="current-password"
                                                    placeholder="••••••••"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Yeni Şifre</label>
                                                <input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    placeholder="••••••••"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium"
                                                    value={formData.password === user.password ? '' : formData.password || ''}
                                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 dark:text-slate-300 mb-2">Yeni Şifre (Tekrar)</label>
                                                <input
                                                    type="password"
                                                    autoComplete="new-password"
                                                    placeholder="••••••••"
                                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-600 transition-all font-medium"
                                                />
                                            </div>
                                        </div>

                                        {/* Forgot Password Section */}
                                        <div className="pt-6 border-t border-gray-200 dark:border-slate-700">
                                            <button
                                                type="button"
                                                onClick={() => setShowForgotPassword(true)}
                                                className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium transition-colors"
                                            >
                                                <LockClosedIcon className="w-5 h-5" />
                                                Şifremi Unuttum
                                            </button>
                                        </div>

                                    </div>
                                )}

                                {activeTab === 'preferences' && (
                                    <div className="space-y-6 animate-fade-in max-w-3xl">
                                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white border-b border-gray-200 dark:border-slate-700 pb-4">Bildirim Tercihleri</h2>
                                        <div className="space-y-4">
                                            {[
                                                "E-posta Bildirimleri",
                                                "SMS Bildirimleri",
                                                "Ders Hatırlatmaları",
                                                "Yeni Ödev/Sınav Bildirimleri",
                                                "Haftalık Rapor Özeti"
                                            ].map((item, i) => (
                                                <div key={i} className="flex items-center justify-between p-4 border border-gray-100 dark:border-slate-700 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                                                    <span className="font-medium text-gray-700 dark:text-slate-300">{item}</span>
                                                    <label className="relative inline-flex items-center cursor-pointer">
                                                        <input type="checkbox" title="Bildirim ayarı" className="sr-only peer" defaultChecked={i < 3} />
                                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'analysis' && user?.role === UserRole.STUDENT && (
                                    <div className="max-w-full">
                                        <ProfileAnalysisSection student={user as Student} />
                                    </div>
                                )}

                                <div className="pt-8 border-t border-gray-100 dark:border-slate-700 flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="px-8 py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-1"
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                                Kaydediliyor...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircleIcon className="w-5 h-5" />
                                                Değişiklikleri Kaydet
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md p-6 animate-fade-in">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">Şifre Sıfırlama</h3>
                            <button
                                title="Kapat"
                                onClick={() => {
                                    setShowForgotPassword(false);
                                    setResetStep('email');
                                    setResetError('');
                                    setResetSuccess('');
                                    setResendTimer(0);
                                }}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                <XCircleIcon className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Step indicators */}
                        <div className="flex items-center justify-center gap-2 mb-6">
                            {['email', 'code', 'newPassword'].map((step, i) => (
                                <div
                                    key={step}
                                    className={`w-3 h-3 rounded-full transition-all ${resetStep === step ? 'bg-blue-600 w-6' :
                                        ['email', 'code', 'newPassword'].indexOf(resetStep) > i ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                                        }`}
                                />
                            ))}
                        </div>

                        {resetError && (
                            <div className="bg-red-50 dark:bg-red-500/20 text-red-600 dark:text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">
                                {resetError}
                            </div>
                        )}

                        {resetSuccess && (
                            <div className="bg-green-50 dark:bg-green-500/20 text-green-600 dark:text-green-300 px-4 py-2 rounded-lg mb-4 text-sm">
                                {resetSuccess}
                            </div>
                        )}

                        {resetStep === 'email' && (
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-slate-400 text-sm">E-posta adresinize doğrulama kodu göndereceğiz.</p>
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    placeholder="E-posta adresiniz"
                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                                <button
                                    onClick={handleRequestCode}
                                    disabled={resetLoading || !resetEmail}
                                    className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50"
                                >
                                    {resetLoading ? 'Gönderiliyor...' : 'Kod Gönder'}
                                </button>
                            </div>
                        )}

                        {resetStep === 'code' && (
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-slate-400 text-sm">E-posta adresinize gönderilen 6 haneli kodu girin.</p>
                                <input
                                    type="text"
                                    value={resetCode}
                                    onChange={(e) => setResetCode(e.target.value)}
                                    placeholder="6 haneli kod"
                                    maxLength={6}
                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all text-center text-2xl tracking-widest"
                                />
                                <button
                                    onClick={handleVerifyCode}
                                    disabled={resetLoading || resetCode.length !== 6}
                                    className="w-full py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50"
                                >
                                    {resetLoading ? 'Doğrulanıyor...' : 'Kodu Doğrula'}
                                </button>
                                
                                <button
                                    onClick={handleRequestCode}
                                    disabled={resetLoading || resendTimer > 0}
                                    className={`w-full py-3 border font-bold rounded-xl transition-all ${resendTimer > 0 ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed dark:bg-slate-700/50 dark:border-slate-600 dark:text-slate-500' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'}`}
                                >
                                    {resendTimer > 0 ? `Kodu Yeniden Gönder (${resendTimer}s)` : 'Kodu Yeniden Gönder'}
                                </button>
                            </div>
                        )}

                        {resetStep === 'newPassword' && (
                            <div className="space-y-4">
                                <p className="text-gray-600 dark:text-slate-400 text-sm">Yeni güçlü şifrenizi belirleyin (en az 8 karakter, büyük/küçük harf, rakam ve sembol).</p>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Yeni güçlü şifre (En az 8 karakter)"
                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />

                                {/* Password rules hints */}
                                <div className="p-3 bg-gray-100 dark:bg-slate-700/50 rounded-xl text-xs space-y-1 text-gray-600 dark:text-slate-300">
                                    <p className="font-semibold text-gray-800 dark:text-white mb-1">Şifre Kriterleri:</p>
                                    <p className={newPassword.length >= 8 ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-slate-400"}>
                                        {newPassword.length >= 8 ? "✓" : "•"} En az 8 karakter
                                    </p>
                                    <p className={/[A-Z]/.test(newPassword) ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-slate-400"}>
                                        {/[A-Z]/.test(newPassword) ? "✓" : "•"} En az bir büyük harf (A-Z)
                                    </p>
                                    <p className={/[a-z]/.test(newPassword) ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-slate-400"}>
                                        {/[a-z]/.test(newPassword) ? "✓" : "•"} En az bir küçük harf (a-z)
                                    </p>
                                    <p className={/\d/.test(newPassword) ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-slate-400"}>
                                        {/\d/.test(newPassword) ? "✓" : "•"} En az bir rakam (0-9)
                                    </p>
                                    <p className={/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword) ? "text-green-600 dark:text-green-400" : "text-gray-500 dark:text-slate-400"}>
                                        {/[!@#$%^&*()_+\-=\[\]{}|;':",.<>?/\\~`]/.test(newPassword) ? "✓" : "•"} En az bir sembol (!@#$% vb.)
                                    </p>
                                </div>

                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Şifreyi tekrar girin"
                                    className="w-full border border-gray-200 dark:border-slate-600 p-4 rounded-xl bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                />
                                <button
                                    onClick={handleResetPassword}
                                    disabled={resetLoading || newPassword.length < 8 || newPassword !== confirmPassword}
                                    className="w-full py-3 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {resetLoading ? 'Kaydediliyor...' : 'Şifreyi Güncelle'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ============================================
// Profile Analysis Section Component
// ============================================

interface ProfileAnalysisSectionProps {
    student: Student;
    consultantMode?: boolean;
}

const getLevelColor = (level: ScaleLevel | undefined) => {
    if (!level) return { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' };
    switch (level) {
        case ScaleLevel.HIGH:
        case ScaleLevel.EFFICIENT:
        case ScaleLevel.GOOD:
            return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
        case ScaleLevel.MEDIUM:
        case ScaleLevel.PARTIALLY_EFFICIENT:
            return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
        case ScaleLevel.LOW:
        case ScaleLevel.INEFFICIENT:
        case ScaleLevel.WEAK:
            return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
        default:
            return { bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200' };
    }
};

const ScaleCard = ({ scale, icon, gradientFrom, gradientTo }: {
    scale: ScaleDetail;
    icon: string;
    gradientFrom: string;
    gradientTo: string;
}) => {
    const [expanded, setExpanded] = useState(false);
    const current = scale.current;
    const levelColors = getLevelColor(current?.level);
    const progress = current ? Math.min((current.score / scale.maxScore) * 100, 100) : 0;

    return (
        <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-800/80 border border-white/50 dark:border-slate-700/50 shadow-lg rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 group">
            {/* Header */}
            <div className={`bg-gradient-to-r ${gradientFrom} ${gradientTo} p-4 text-white`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">{icon}</span>
                        <h4 className="font-bold text-lg">{scale.name}</h4>
                    </div>
                    {current && (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${levelColors.bg} ${levelColors.text}`}>
                            {current.level}
                        </span>
                    )}
                </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
                {current ? (
                    <>
                        {/* Score & Progress */}
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-3xl font-black text-gray-800 dark:text-white">{current.score}</span>
                                <span className="text-sm text-gray-400 dark:text-slate-500">/ {scale.maxScore}</span>
                            </div>
                            <div className="h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                {/* eslint-disable-next-line react/no-inline-styles */}
                                <div
                                    className={`h-full rounded-full bg-gradient-to-r ${gradientFrom} ${gradientTo} transition-all duration-500 w-[var(--prof-view-prog)]`}
                                    style={{ '--prof-view-prog': `${progress}%` } as React.CSSProperties}
                                />
                            </div>
                        </div>

                        {/* System Comment */}
                        {current.systemComment && (
                            <p className="text-sm text-gray-600 dark:text-slate-400 bg-gray-50 dark:bg-slate-700/50 p-3 rounded-xl border border-gray-100 dark:border-slate-700">
                                💡 {current.systemComment}
                            </p>
                        )}

                        {/* Sub-scales */}
                        {current.details?.subScales && current.details.subScales.length > 0 && (
                            <div>
                                <button
                                    onClick={() => setExpanded(!expanded)}
                                    className="text-sm text-blue-600 font-bold flex items-center gap-1 hover:text-blue-700"
                                >
                                    {expanded ? '▼' : '▶'} Alt Ölçekler ({current.details.subScales.length})
                                </button>
                                {expanded && (
                                    <div className="mt-3 space-y-2 animate-fade-in">
                                        {current.details.subScales.map((sub, i) => {
                                            const subColors = getLevelColor(sub.level);
                                            return (
                                                <div key={i} className={`p-3 rounded-xl ${subColors.bg} border ${subColors.border}`}>
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-bold text-sm text-gray-800">{sub.name}</span>
                                                        <span className={`text-xs font-bold ${subColors.text}`}>{sub.score} puan</span>
                                                    </div>
                                                    {sub.comment && (
                                                        <p className="text-xs text-gray-500 mt-1">{sub.comment}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Date */}
                        <p className="text-xs text-gray-400 text-right">
                            📅 {new Date(current.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                    </>
                ) : (
                    <div className="text-center py-6 text-gray-400">
                        <span className="text-4xl mb-2 block opacity-30">📋</span>
                        <p className="text-sm">Henüz ölçek sonucu yok</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ProfileAnalysisSection = ({ student, consultantMode = false }: ProfileAnalysisSectionProps) => {
    const scales = student.scales;

    if (!scales) {
        return (
            <div className="space-y-8 animate-fade-in text-center py-12 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-gray-300 dark:border-slate-700">
                <div className="text-6xl mb-4">📋</div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">Ölçek Verisi Bulunamadı</h3>
                <p className="text-gray-500 dark:text-slate-400">Bu öğrenci için henüz tanımlanmış ölçek sonucu bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl p-6 text-white shadow-xl">
                <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                        <ChartBarIcon className="w-8 h-8" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-white">Profil Analizi</h2>
                        <p className="text-white/80">Tanıma formu verileri ve ölçek sonuçları</p>
                    </div>
                </div>
            </div>

            {/* Temel Bilgiler */}
            <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-800/80 border border-white/50 dark:border-slate-700/50 shadow-xl rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-40 h-40 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 rounded-full blur-3xl -ml-20 -mt-20"></div>
                <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-2xl">🎓</span> Temel Bilgiler
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {/* Okul */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">🏫</span>
                                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">Okul</span>
                            </div>
                            <p className="text-sm font-bold text-gray-800 dark:text-slate-200">{student.school || 'Belirtilmedi'}</p>
                        </div>
                        {/* Sınıf */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">📚</span>
                                <span className="text-xs font-bold text-blue-700 dark:text-blue-400">Sınıf</span>
                            </div>
                            <p className="text-sm font-bold text-gray-800 dark:text-slate-200">{student.grade || 'Belirtilmedi'}</p>
                        </div>
                        {/* Alan */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 p-4 rounded-2xl border border-purple-100 dark:border-purple-900/30">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">🎯</span>
                                <span className="text-xs font-bold text-purple-700 dark:text-purple-400">Alan</span>
                            </div>
                            <p className="text-sm font-bold text-gray-800 dark:text-slate-200">{student.department || 'Belirtilmedi'}</p>
                        </div>
                        {/* Hedef */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 p-4 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">🎯</span>
                                <span className="text-xs font-bold text-amber-700 dark:text-amber-400">Hedef</span>
                            </div>
                            <p className="text-sm font-bold text-gray-800 dark:text-slate-200">{student.goal || 'Belirtilmedi'}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Personal Recognition Summary */}
            <div className="backdrop-blur-xl bg-white/80 dark:bg-slate-800/80 border border-white/50 dark:border-slate-700/50 shadow-xl rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl -mr-20 -mt-20"></div>
                <div className="relative z-10">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
                        <span className="text-2xl">👤</span> Kişisel Tanıma Özeti
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Sevdiği Dersler */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">📚</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Sevdiği Dersler</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {student.academicInterests && student.academicInterests.length > 0 ? (
                                    student.academicInterests.map((interest, i) => (
                                        <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                                            {interest}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-sm italic">Belirtilmemiş</span>
                                )}
                            </div>
                        </div>

                        {/* Güçlü Alanlar */}
                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20 p-4 rounded-2xl border border-emerald-100 dark:border-emerald-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">💪</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Güçlü Olduğu</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {student.strongSubjects && student.strongSubjects.length > 0 ? (
                                    student.strongSubjects.map((subject, i) => (
                                        <span key={i} className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                                            {subject}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-sm italic">Belirtilmemiş</span>
                                )}
                            </div>
                        </div>

                        {/* Zayıf Alanlar */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20 p-4 rounded-2xl border border-amber-100 dark:border-amber-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">⚠️</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Geliştirilmesi Gereken</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {student.weakSubjects && student.weakSubjects.length > 0 ? (
                                    student.weakSubjects.map((subject, i) => (
                                        <span key={i} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                                            {subject}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-gray-400 text-sm italic">Belirtilmemiş</span>
                                )}
                            </div>
                        </div>

                        {/* Boş Zaman */}
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 p-4 rounded-2xl border border-purple-100 dark:border-purple-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">⏰</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Günlük Boş Zaman</span>
                            </div>
                            <p className="text-lg font-bold text-purple-700 dark:text-purple-400">
                                {student.freeTime || 'Belirtilmemiş'}
                            </p>
                        </div>

                        {/* Motivasyon */}
                        <div className="bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/20 dark:to-red-950/20 p-4 rounded-2xl border border-rose-100 dark:border-rose-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">🔥</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Motivasyon Seviyesi</span>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                    {/* eslint-disable-next-line react/no-inline-styles */}
                                    <div
                                        className="h-full bg-gradient-to-r from-rose-500 to-red-500 rounded-full transition-all duration-500 w-[var(--mot-prog)]"
                                        style={{ '--mot-prog': `${(student.motivationLevel || 5) * 10}%` } as React.CSSProperties}
                                    />
                                </div>
                                <span className="text-2xl font-black text-rose-600 dark:text-rose-400">{student.motivationLevel || 5}/10</span>
                            </div>
                        </div>

                        {/* Başlangıç Netleri */}
                        <div className="bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-950/20 dark:to-sky-950/20 p-4 rounded-2xl border border-cyan-100 dark:border-cyan-900/30">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xl">📊</span>
                                <span className="font-bold text-gray-700 dark:text-slate-300">Başlangıç Netleri</span>
                            </div>
                            <div className="flex gap-4">
                                {student.initialNets?.tyt !== undefined && (
                                    <div className="text-center">
                                        <span className="text-2xl font-black text-cyan-600">{student.initialNets.tyt}</span>
                                        <p className="text-xs text-gray-500">TYT</p>
                                    </div>
                                )}
                                {student.initialNets?.ayt !== undefined && (
                                    <div className="text-center">
                                        <span className="text-2xl font-black text-sky-600">{student.initialNets.ayt}</span>
                                        <p className="text-xs text-gray-500">AYT</p>
                                    </div>
                                )}
                                {student.initialNets?.ydt !== undefined && (
                                    <div className="text-center">
                                        <span className="text-2xl font-black text-blue-600">{student.initialNets.ydt}</span>
                                        <p className="text-xs text-gray-500">YDT</p>
                                    </div>
                                )}
                                {!student.initialNets?.tyt && !student.initialNets?.ayt && !student.initialNets?.ydt && (
                                    <span className="text-gray-400 text-sm italic">Belirtilmemiş</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scale Cards */}
            <div>
                <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <span className="text-2xl">📋</span> Ölçek Sonuçları
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ScaleCard
                        scale={scales.studyHabits}
                        icon="📚"
                        gradientFrom="from-indigo-500"
                        gradientTo="to-purple-500"
                    />
                    <ScaleCard
                        scale={scales.anxiety}
                        icon="😰"
                        gradientFrom="from-amber-500"
                        gradientTo="to-orange-500"
                    />
                </div>
            </div>

            {/* General Assessment */}
            <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 shadow-xl rounded-3xl p-6 text-white">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <span className="text-2xl">💡</span> Genel Değerlendirme
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Strengths */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                        <h4 className="font-bold text-emerald-400 mb-3 flex items-center gap-2">
                            <CheckCircleIcon className="w-5 h-5" /> Güçlü Yönler
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-300">
                            {scales.studyHabits.current?.level === ScaleLevel.EFFICIENT && (
                                <li>• Verimli ders çalışma alışkanlıkları</li>
                            )}
                            {scales.anxiety.current?.level === ScaleLevel.LOW && (
                                <li>• Sınav kaygısı kontrol altında</li>
                            )}
                            {!scales.studyHabits.current && !scales.anxiety.current && (
                                <li className="text-gray-500 italic">Ölçekler tamamlandığında güçlü yönler burada görünecek</li>
                            )}
                        </ul>
                    </div>

                    {/* Areas to Improve */}
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
                        <h4 className="font-bold text-amber-400 mb-3 flex items-center gap-2">
                            <LightBulbIcon className="w-5 h-5" /> Geliştirilmesi Gereken
                        </h4>
                        <ul className="space-y-2 text-sm text-gray-300">
                            {scales.anxiety.current?.level === ScaleLevel.HIGH && (
                                <li>• Sınav kaygısı yüksek - nefes egzersizleri önerilir</li>
                            )}
                            {scales.studyHabits.current?.level === ScaleLevel.INEFFICIENT && (
                                <li>• Çalışma alışkanlıkları geliştirilmeli</li>
                            )}
                            {!scales?.anxiety?.current && !scales?.studyHabits?.current && (
                                <li className="text-gray-500 italic">Ölçekler tamamlandığında öneriler burada görünecek</li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};
